/**
 * AdaptiveQualityController — measure rolling FPS, step quality up/down.
 *
 * Knob ladder (priority order — earlier knobs cheaper to twist):
 *
 *   0.  renderTargetScale (canvas DPR clamp): 1.0 → 0.85 → 0.7 → 0.5
 *   1.  splatBudget: cut by 25% per step (min 250K)
 *   2.  lodBaseDistance: halve (closer LOD transitions — fewer splats at distance)
 *   3.  minPixelSize: 1 → 2 → 4 → 8 (drop sub-pixel splats)
 *   4.  (reserved for bloom-off once compose pipeline lands)
 *   5.  (reserved for SH bands once we wire SH controls)
 *
 * Trigger:
 *   - FPS < target  for > 1.0s  → step DOWN one knob
 *   - FPS > target × 1.3 for > 2.0s → step UP one knob
 *   - never adjust within 1.0s of the previous adjustment (no thrash)
 *
 * Public API:
 *   new AdaptiveQualityController(app, scene, profile, opts).start()
 *   .setMode('auto' | 'high' | 'medium' | 'low' | 'off')   manual override
 *   .currentLevel  // diagnostic: index into the knob ladder
 *
 * No pc.CameraFrame manipulation yet — compose pipeline is a separate
 * milestone. When it lands, add steps 4-6 as additional rungs.
 */

import * as pc from 'playcanvas';

import type { DeviceClass, DeviceProfile } from './device-class';

export type QualityMode = 'auto' | 'high' | 'medium' | 'low' | 'off';

interface Knobs {
    /** 0..1 — canvas resolution scale (devicePixelRatio multiplier). */
    rtScale: number;
    /** Splats allowed in the work buffer. */
    splatBudget: number;
    /** Per-entity LOD distance scaling — multiplied into the original value. */
    lodMul: number;
    /** Min on-screen pixel size for a splat. */
    minPixelSize: number;
}

const TARGET_FPS = 30;
const FPS_HIGH_RATIO = 1.3;
const STEP_INTERVAL_MS = 2000;     // was 1000 — give each adjustment time to settle
const DOWN_DWELL_MS = 2500;        // was 1000 — don't react to single-frame stalls
const UP_DWELL_MS = 2000;
const SAMPLE_WINDOW = 60;
// Skip the first N ms after start() so the adaptive controller doesn't react
// to LOD-streaming chunk decode stalls and other one-time boot costs.
const WARMUP_MS = 3000;

// Knob ladder — level 0 = max quality, higher index = lower quality.
//
// minPixelSize is intentionally pinned to 1 across all rungs. Raising it
// produces visible "splat borders" — individual Gaussians forced larger than
// their natural footprint, breaking the blend. It's the most visually
// destructive knob and the user notices instantly. The other three knobs
// (DPR scale, splatBudget, lodBaseDistance multiplier) degrade more gracefully.
const LADDER: Knobs[] = [
    { rtScale: 1.00, splatBudget: 8_000_000, lodMul: 1.0,  minPixelSize: 1 },  // 0 max — editor-parity (full)
    { rtScale: 0.90, splatBudget: 3_500_000, lodMul: 1.0,  minPixelSize: 1 },  // 1
    { rtScale: 0.80, splatBudget: 3_000_000, lodMul: 0.85, minPixelSize: 1 },  // 2
    { rtScale: 0.75, splatBudget: 2_500_000, lodMul: 0.75, minPixelSize: 1 },  // 3
    { rtScale: 0.70, splatBudget: 2_000_000, lodMul: 0.6,  minPixelSize: 1 },  // 4
    { rtScale: 0.65, splatBudget: 1_500_000, lodMul: 0.5,  minPixelSize: 1 },  // 5 mobile mid
    { rtScale: 0.55, splatBudget: 1_000_000, lodMul: 0.4,  minPixelSize: 1 },  // 6
    { rtScale: 0.50, splatBudget:   750_000, lodMul: 0.3,  minPixelSize: 1 }   // 7 floor
];

// Starting rung per device class.
const START_LEVEL: Record<DeviceClass, number> = {
    high: 0,
    medium: 2,
    low: 5
};

interface SceneRef {
    /** Per-splat baseline lodBaseDistance, captured at first apply. */
    baselineLodBaseDistance: Map<pc.Entity, number>;
}

export interface AdaptiveOptions {
    targetFps?: number;
    /** Manual mode at boot (default 'auto'). */
    mode?: QualityMode;
    /** Rung to start 'auto' from instead of the device-class default.
     *  The stand demo passes 0: a device we know starts at editor parity
     *  and only steps down if it really stutters. */
    startLevel?: number;
}

export class AdaptiveQualityController {

    readonly app: pc.AppBase;

    private profile: DeviceProfile;

    private splatEntities: pc.Entity[];

    private sceneRef: SceneRef;

    private mode: QualityMode;

    private targetFps: number;

    private startLevel: number | null;

    private level: number;

    /** Last N frame times (ms). Newest at end. */
    private frameTimes: number[] = [];

    /** Wallclock at start(), ms. Used for warmup gating. */
    private startedAtMs = 0;

    /** Last wallclock when we adjusted, ms. */
    private lastAdjustMs = 0;

    /** When FPS first dropped below target, ms. 0 = not currently below. */
    private belowSinceMs = 0;

    /** When FPS first rose above target*ratio, ms. 0 = not currently above. */
    private aboveSinceMs = 0;

    private detachFn: (() => void) | null = null;

    constructor(
        app: pc.AppBase,
        splatEntities: pc.Entity[],
        profile: DeviceProfile,
        opts: AdaptiveOptions = {}
    ) {
        this.app = app;
        this.profile = profile;
        this.splatEntities = splatEntities;
        this.targetFps = opts.targetFps ?? TARGET_FPS;
        this.mode = opts.mode ?? 'auto';
        this.startLevel = opts.startLevel ?? null;

        // Capture per-entity baseline lod distance so lodMul can scale it.
        this.sceneRef = { baselineLodBaseDistance: new Map() };
        for (const e of splatEntities) {
            const gs = e.gsplat as unknown as { lodBaseDistance?: number };
            if (typeof gs.lodBaseDistance === 'number') {
                this.sceneRef.baselineLodBaseDistance.set(e, gs.lodBaseDistance);
            }
        }

        // Initial level from mode or device class.
        this.level = this.modeToLevel(this.mode);
        this.applyLevel();
    }

    /** Begin per-frame measurement + adjustment. */
    start(): void {
        if (this.detachFn) return;
        this.startedAtMs = performance.now();
        const onFrame = () => this.onFrameRender();
        this.app.on('framerender', onFrame);
        this.detachFn = () => this.app.off('framerender', onFrame);
    }

    stop(): void {
        if (this.detachFn) { this.detachFn(); this.detachFn = null; }
    }

    /**
     * Forget the measurement window.
     *
     * For deliberate, one-off main-thread stalls that are NOT the device failing
     * to keep up — today that means the screenshot capture, which renders out of
     * band and then encodes a PNG synchronously inside the click (see
     * capture/capture-frame.ts). The sampler only sees a hole in the frame-time
     * stream, and a single ~300ms hole drags the 60-frame windowed average from
     * 30fps to ~26 and holds it there for the ~2s the sample takes to age out —
     * past DOWN_DWELL_MS, so a phone already grazing target would visibly drop a
     * rung right after the user pressed save. Clearing the window costs one
     * SAMPLE_WINDOW of no adjustments, which is exactly the desired behaviour.
     */
    resetSamples(): void {
        this.frameTimes.length = 0;
        this.belowSinceMs = 0;
        this.aboveSinceMs = 0;
    }

    /** Manual override. 'auto' resumes FPS-driven control. */
    setMode(mode: QualityMode): void {
        this.mode = mode;
        const newLevel = this.modeToLevel(mode);
        if (newLevel !== this.level) {
            this.level = newLevel;
            this.applyLevel();
        }
        this.belowSinceMs = 0;
        this.aboveSinceMs = 0;
    }

    get currentMode(): QualityMode { return this.mode; }

    get currentLevel(): number { return this.level; }

    get currentFps(): number {
        if (this.frameTimes.length < 2) return 0;
        const span = this.frameTimes[this.frameTimes.length - 1] - this.frameTimes[0];
        return span > 0 ? ((this.frameTimes.length - 1) * 1000) / span : 0;
    }

    // ── internals ────────────────────────────────────────────────────────

    private modeToLevel(mode: QualityMode): number {
        switch (mode) {
            case 'high':   return 0;
            case 'medium': return 3;
            case 'low':    return 5;
            case 'off':    return this.level;       // freeze where we are
            case 'auto':   return this.startLevel ?? START_LEVEL[this.profile.cls];
        }
    }

    private onFrameRender(): void {
        const now = performance.now();
        this.frameTimes.push(now);
        if (this.frameTimes.length > SAMPLE_WINDOW) this.frameTimes.shift();
        if (this.mode !== 'auto') return;
        if (this.frameTimes.length < SAMPLE_WINDOW) return;     // not enough data yet
        if (now - this.startedAtMs < WARMUP_MS) return;         // warmup — ignore boot stalls

        const fps = this.currentFps;
        const belowTarget = fps < this.targetFps;
        const aboveHigh = fps > this.targetFps * FPS_HIGH_RATIO;

        if (belowTarget) {
            if (this.belowSinceMs === 0) this.belowSinceMs = now;
            this.aboveSinceMs = 0;
        } else if (aboveHigh) {
            if (this.aboveSinceMs === 0) this.aboveSinceMs = now;
            this.belowSinceMs = 0;
        } else {
            this.belowSinceMs = 0;
            this.aboveSinceMs = 0;
        }

        if (now - this.lastAdjustMs < STEP_INTERVAL_MS) return;

        if (this.belowSinceMs && now - this.belowSinceMs >= DOWN_DWELL_MS && this.level < LADDER.length - 1) {
            this.level += 1;
            this.lastAdjustMs = now;
            this.belowSinceMs = 0;
            this.applyLevel();
        } else if (this.aboveSinceMs && now - this.aboveSinceMs >= UP_DWELL_MS && this.level > 0) {
            this.level -= 1;
            this.lastAdjustMs = now;
            this.aboveSinceMs = 0;
            this.applyLevel();
        }
    }

    private applyLevel(): void {
        const k = LADDER[this.level];

        // 0. Canvas resolution scale. PC's maxPixelRatio caps the DPR effectively.
        const baseDpr = this.profile.dpr;
        const dev = this.app.graphicsDevice as unknown as { maxPixelRatio: number };
        dev.maxPixelRatio = Math.max(0.5, baseDpr * k.rtScale);
        this.app.resizeCanvas();

        // 1. Splat budget.
        const gs = this.app.scene.gsplat;
        gs.splatBudget = k.splatBudget;

        // 2. Per-entity LOD distance — scale from each splat's baseline.
        for (const [entity, baseline] of this.sceneRef.baselineLodBaseDistance) {
            const eGs = entity.gsplat as unknown as { lodBaseDistance?: number } | null;
            if (eGs) eGs.lodBaseDistance = baseline * k.lodMul;
        }

        // 3. Min pixel size.
        gs.minPixelSize = k.minPixelSize;
    }
}
